
import React, { useState } from "react";
import Sidebar from "../Sidebar";
import "./CityWiseLead.css";

interface CityStats {
  city: string;
  total: number;
  active: number;
  invalid: number;
  notQ: number;
  siteV: number;
  f2f: number;
  scheduled: number;
  token: number;
  trans: number;
}

interface SummaryStats {
  totalMembers: number;
  totalLeads: number;
  scheduled: number;
  f2fDone: number;
  siteVisit: number;
  tokenDone: number;
  transactions: number;
  invalid: number;
}

const CityWiseLead: React.FC = () => {
  const [selectedPartner, setSelectedPartner] = useState("all");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Static Summary Data
  const summary: SummaryStats = {
    totalMembers: 8,
    totalLeads: 74,
    scheduled: 8,
    f2fDone: 10,
    siteVisit: 8,
    tokenDone: 8,
    transactions: 17,
    invalid: 8,
  };

  // Static City-wise Data with updated values from image
  const cityData: CityStats[] = [
    {
      city: "Pune",
      total: 12,
      active: 2,
      invalid: 2,
      notQ: 1,
      siteV: 3,
      f2f: 1,
      scheduled: 1,
      token: 1,
      trans: 1,
    },
    {
      city: "Mumbai",
      total: 26,
      active: 4,
      invalid: 8,
      notQ: 1,
      siteV: 3,
      f2f: 3,
      scheduled: 3,
      token: 3,
      trans: 1,
    },
    {
      city: "Bengaluru",
      total: 17,
      active: 1,
      invalid: 3,
      notQ: 2,
      siteV: 3,
      f2f: 2,
      scheduled: 2,
      token: 2,
      trans: 2,
    },
    {
      city: "Dubai",
      total: 19,
      active: 3,
      invalid: 4,
      notQ: 0,
      siteV: 1,
      f2f: 2,
      scheduled: 3,
      token: 2,
      trans: 4,
    },
  ];

  // Status cards data
  const statusCards = [
    { label: "Total Members", value: summary.totalMembers, color: "#4A90D9" },
    { label: "Total Leads", value: summary.totalLeads, color: "#27AE60" },
    { label: "Scheduled", value: summary.scheduled, color: "#F39C12" },
    { label: "F2F Done", value: summary.f2fDone, color: "#8E44AD" },
    { label: "Site Visit", value: summary.siteVisit, color: "#1ABC9C" },
    { label: "Token Done", value: summary.tokenDone, color: "#E67E22" },
    { label: "Transactions", value: summary.transactions, color: "#2ECC71" },
    { label: "Invalid", value: summary.invalid, color: "#E74C3C" },
  ];

  // Get color for city circle based on total leads
  const getCityColor = (total: number) => {
    if (total >= 25) return "#E74C3C"; // Red for very high
    if (total >= 18) return "#F39C12"; // Orange for high
    if (total >= 13) return "#3498DB"; // Blue for medium
    return "#27AE60"; // Green for low
  };

  // Get row color based on city
  const getRowColor = (city: string) => {
    const colors: { [key: string]: string } = {
      'Pune': '#E8F5E9', // Light Green
      'Mumbai': '#FFF3E0', // Light Orange
      'Bengaluru': '#E3F2FD', // Light Blue
      'Dubai': '#FCE4EC', // Light Pink
    };
    return colors[city] || '#FFFFFF';
  };

  // Get row hover color based on city
  const getRowHoverColor = (city: string) => {
    const colors: { [key: string]: string } = {
      'Pune': '#C8E6C9', // Darker Green
      'Mumbai': '#FFE0B2', // Darker Orange
      'Bengaluru': '#BBDEFB', // Darker Blue
      'Dubai': '#F8BBD0', // Darker Pink
    };
    return colors[city] || '#F5F5F5';
  };

  // Get border color for row
  const getRowBorderColor = (city: string) => {
    const colors: { [key: string]: string } = {
      'Pune': '#4CAF50',
      'Mumbai': '#FF9800',
      'Bengaluru': '#2196F3',
      'Dubai': '#E91E63',
    };
    return colors[city] || '#DDDDDD';
  };



  return (
    <div className="city-wise-lead-page">
      {/* Sidebar */}
      <Sidebar  />

      {/* Main Content */}
      <div className={`city-wise-lead-container ${sidebarCollapsed ? 'expanded' : ''}`}>
        {/* Header */}
        <div className="header-section">
          <h2>City-wise Leads Overview</h2>
          <p className="subtitle">Monitor leads by city and location</p>
        </div>

        {/* Partner Filter */}
        <div className="filter-section">
          <select
            value={selectedPartner}
            onChange={(e) => setSelectedPartner(e.target.value)}
            className="partner-filter"
          >
            <option value="all">All Partners</option>
            <option value="partner1">Partner 1</option>
            <option value="partner2">Partner 2</option>
          </select>
        </div>

        {/* Overall Status Summary */}
        <div className="summary-section">
          <h3>Overall Status Summary</h3>
          <div className="status-cards">
            {statusCards.map((card, index) => (
              <div
                key={index}
                className="status-card"
                style={{ borderLeftColor: card.color }}
              >
                <span className="status-value">{card.value}</span>
                <span className="status-label">{card.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* City-wise Data Table with Row-wise Colors */}
        <div className="table-section">
          <h3>City-wise Leads Distribution</h3>
          <div className="table-wrapper">
            <table className="city-table">
              <thead>
                <tr>
                  <th>CITY</th>
                  <th>TOTAL</th>
                  <th>ACTIVE</th>
                  <th>INVALID</th>
                  <th>NOT Q</th>
                  <th>SITE V</th>
                  <th>F2F</th>
                  <th>SCHED</th>
                  <th>TOKEN</th>
                  <th>TRANS</th>
                </tr>
              </thead>
              <tbody>
                {cityData.map((city, index) => (
                  <tr 
                    key={index}
                    className="city-row"
                    style={{
                      backgroundColor: getRowColor(city.city),
                      borderLeft: `4px solid ${getRowBorderColor(city.city)}`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = getRowHoverColor(city.city);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = getRowColor(city.city);
                    }}
                  >
                    <td className="city-name">
                      <div className="city-circle-container">
                       
                        <span>{city.city}</span>
                      </div>
                    </td>
                    <td>
                      <div className="value-circle" style={{ backgroundColor: getCityColor(city.total) }}>
                        {city.total}
                      </div>
                    </td>
                    <td>
                      <div className="value-circle" style={{ backgroundColor: getCityColor(city.active * 5 + 5) }}>
                        {city.active}
                      </div>
                    </td>
                    <td>
                      <div className="value-circle" style={{ backgroundColor: getCityColor(city.invalid * 4 + 4) }}>
                        {city.invalid}
                      </div>
                    </td>
                    <td>
                      <div className="value-circle" style={{ backgroundColor: getCityColor(city.notQ * 8 + 8) }}>
                        {city.notQ}
                      </div>
                    </td>
                    <td>
                      <div className="value-circle" style={{ backgroundColor: getCityColor(city.siteV * 5 + 5) }}>
                        {city.siteV}
                      </div>
                    </td>
                    <td>
                      <div className="value-circle" style={{ backgroundColor: getCityColor(city.f2f * 8 + 8) }}>
                        {city.f2f}
                      </div>
                    </td>
                    <td>
                      <div className="value-circle" style={{ backgroundColor: getCityColor(city.scheduled * 8 + 8) }}>
                        {city.scheduled}
                      </div>
                    </td>
                    <td>
                      <div className="value-circle" style={{ backgroundColor: getCityColor(city.token * 8 + 8) }}>
                        {city.token}
                      </div>
                    </td>
                    <td>
                      <div className="value-circle" style={{ backgroundColor: getCityColor(city.trans * 5 + 5) }}>
                        {city.trans}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityWiseLead;